import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNotifications } from '../../../hooks/useNotifications';
import './checklist.css';
import { BACKEND_URL } from '../../../config';
import { useDataLayerValue } from '../../../contextAPI/DataLayer';
import { ADMINISTRATOR_ROLE_ID } from '../../../constants/AdministratorConstants';

import {
  editPrompt,
  fetchAllChecklistHistory,
  fetchInitialPrompt,
  moveChecklistToRepository
} from '../../../constants/requests';
import { useLoading } from '../../../hooks/useLoading';
const ChecklistGenerator = () => {
  const [{ _id, role, roleId }] = useDataLayerValue();
  const { showNotifications } = useNotifications();
  const { request } = useLoading();

  // Check if user has admin role
  const isAdmin = () => {
    return role === 'Administrator' || roleId === ADMINISTRATOR_ROLE_ID;
  };
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [checklistType, setChecklistType] = useState('REGULATION');
  const [isUploading, setIsUploading] = useState(false);
  const [tocStartPage, setTocStartPage] = useState(1);
  const [tocEndPage, setTocEndPage] = useState(5);
  const [checklistHistory, setChecklistHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // Prompt management state
  const [promptData, setPromptData] = useState(null);
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editedPromptContent, setEditedPromptContent] = useState('');
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is PDF
      if (file.type !== 'application/pdf') {
        showNotifications('error', 'Please upload a PDF file only', 5000);
        return;
      }

      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showNotifications('error', 'File size should be less than 10MB', 5000);
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
      // Auto-populate document title with filename (without extension)
      const titleFromFile = file.name.replace(/\.[^/.]+$/, '');
      setDocumentTitle(titleFromFile);
      console.log('Selected file:', file);
      showNotifications('success', 'PDF file selected successfully', 3000);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showNotifications('error', 'Please select a PDF file first', 5000);
      return;
    }

    if (!documentTitle || !documentTitle.trim()) {
      showNotifications('error', 'Please enter a document title', 5000);
      return;
    }

    if (tocEndPage < tocStartPage) {
      showNotifications(
        'error',
        'End page cannot be less than start page',
        5000
      );
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', documentTitle.trim());
      formData.append(
        'description',
        `Compliance ${checklistType.toLowerCase()} PDF for Checklist Generation: ${documentTitle.trim()}`
      );
      formData.append(
        'data',
        JSON.stringify({
          type: 'compliance_circular',
          checklistType: checklistType
        })
      );

      const response = await fetch(BACKEND_URL + '/documents', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(
          `Upload failed: ${response.status} ${response.statusText}`
        );
      }

      const uploadResult = await response.json();
      console.log('Document upload result:', uploadResult);

      try {
        const checklistResponse = await fetch(
          BACKEND_URL + '/checklist/create',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': `${localStorage.getItem('token')}`,
              employeeId: _id
            },
            body: JSON.stringify({
              sourceDocumentId: uploadResult.body._id,
              tocPages: generatePageRange()
            })
          }
        );

        console.log(checklistResponse);

        if (checklistResponse.ok) {
          const checklistResult = await checklistResponse.json();
          console.log('Checklist creation result:', checklistResult);

          showNotifications(
            'success',
            `PDF uploaded and checklist generation initiated successfully!`,
            7000
          );

          fetchChecklistHistory();
        } else {
          console.error('Checklist creation failed:', checklistResponse.status);
          showNotifications(
            'warning',
            `PDF uploaded successfully but checklist generation failed. Document ID: ${uploadResult.documentId || uploadResult.id
            }`,
            5000
          );
        }
      } catch (checklistError) {
        console.error('Checklist creation error:', checklistError);
        showNotifications(
          'warning',
          `PDF uploaded successfully but checklist generation encountered an error. Document ID: ${uploadResult.documentId || uploadResult.id
          }`,
          5000
        );
      }

      // Reset form
      setSelectedFile(null);
      setFileName('');
      setChecklistType('REGULATION');
    } catch (error) {
      console.error('Upload error:', error);
      showNotifications(
        'error',
        'Failed to upload file. Please try again.',
        5000
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleChecklistTypeChange = (event) => {
    setChecklistType(event.target.value);
  };

  const fetchPrompt = async () => {
    setIsLoadingPrompt(true);
    try {
      const response = await request(() => fetchInitialPrompt());

      if (response && response.message && !response.data) {
        throw new Error(response.message);
      }

      if (response && response.data && response.status === 200) {
        const promptObject = response.data.data;
        const content = promptObject?.content;
        if (content) {
          setPromptData(promptObject);
          setEditedPromptContent(content);
          showNotifications('success', 'Prompt loaded successfully.', 3000);
        } else {
          console.error('No content found in prompt object');
          showNotifications('error', 'No prompt content found.', 5000);
        }
      } else {
        const errorMessage =
          response?.data?.message ||
          response?.message ||
          'Failed to fetch prompt';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error fetching prompt:', error);
      showNotifications(
        'error',
        `Failed to load prompt: ${error.message}`,
        5000
      );
    } finally {
      setIsLoadingPrompt(false);
    }
  };

  const savePrompt = async () => {
    if (!isAdmin()) {
      showNotifications(
        'error',
        'Access denied: Admin privileges required',
        3000
      );
      return;
    }

    if (!promptData || !editedPromptContent.trim()) {
      showNotifications('error', 'Prompt content cannot be empty', 3000);
      return;
    }

    setIsSavingPrompt(true);
    try {
      const payload = {
        content: editedPromptContent.trim()
      };

      const response = await request(() => editPrompt(payload));

      if (response && response.data) {
        setPromptData((prev) => ({
          ...prev,
          content: editedPromptContent.trim()
        }));
        setIsEditingPrompt(false);
        showNotifications('success', 'Prompt saved successfully', 3000);
      } else {
        throw new Error('Failed to save prompt');
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      showNotifications(
        'error',
        'Failed to save prompt. Please try again.',
        5000
      );
    } finally {
      setIsSavingPrompt(false);
    }
  };

  const handleEditPrompt = () => {
    if (!isAdmin()) {
      showNotifications(
        'error',
        'Access denied: Admin privileges required',
        3000
      );
      return;
    }
    setIsEditingPrompt(true);
    setEditedPromptContent(promptData?.content || '');
  };

  const handleCancelEdit = () => {
    if (!isAdmin()) {
      showNotifications(
        'error',
        'Access denied: Admin privileges required',
        3000
      );
      return;
    }
    const hasChanges = editedPromptContent !== (promptData?.content || '');

    if (hasChanges) {
      const confirmCancel = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel editing?'
      );
      if (!confirmCancel) {
        return;
      }
    }

    setIsEditingPrompt(false);
    setEditedPromptContent(promptData?.content || '');
  };

  const fetchChecklistHistory = async () => {
    setIsLoadingHistory(true);
    setHistoryError(null);

    try {
      const response = await request(() => fetchAllChecklistHistory());

      if (response && response.message && !response.data) {
        throw new Error(response.message);
      }

      if (response && response.data && response.status === 200) {
        setChecklistHistory(response.data.data);
      } else {
        throw new Error(
          response.message || 'Failed to fetch checklist history'
        );
      }
    } catch (error) {
      console.error('Error fetching checklist history:', error);
      setHistoryError(error.message);
      showNotifications('error', 'Failed to load checklist history', 5000);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await fetchPrompt();
      await fetchChecklistHistory();
    };

    initializeData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'PROCESSING':
        return 'warning';
      case 'PENDING':
        return 'info';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getChecklistTypeDisplay = (type) => {
    switch (type) {
      case 'REGULATION':
        return 'Regulation';
      case 'CIRCULAR':
        return 'Circular';
      default:
        return type;
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setFileName('');
    setDocumentTitle('');
    setTocStartPage(1);
    setTocEndPage(5);
    setChecklistType('REGULATION');
    const fileInput = document.getElementById('pdf-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const generatePageRange = () => {
    const pages = [];
    for (let i = tocStartPage; i <= tocEndPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleMoveToRepository = async (checklistId) => {
    try {
      const response = await request(() =>
        moveChecklistToRepository(checklistId, _id)
      );

      if (response && response.data && response.data.success) {
        showNotifications(
          'success',
          'Checklist successfully moved to repository!',
          5000
        );
        fetchChecklistHistory();
      } else {
        const errorMessage =
          response?.data?.message || 'Failed to move checklist to repository';
        showNotifications('error', errorMessage, 5000);
      }
    } catch (error) {
      console.error('Error moving checklist to repository:', error);
      showNotifications(
        'error',
        'Failed to move checklist to repository',
        5000
      );
    }
  };

  return (
    <div>
      <h1
        style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '2rem' }}
      >
        Compliance Document Checklist Generator
      </h1>

      {/* AI Prompt Display Box */}
      <Box
        sx={{
          marginBottom: '2rem',
          padding: '1.5rem',
          border: '2px solid #e3f2fd',
          borderRadius: '12px',
          backgroundColor: '#f8faff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: '#1565c0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {promptData?.title || 'AI Checklist Generation Prompt'}
          </Typography>

          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            {isAdmin() &&
              (!isEditingPrompt ? (
                <IconButton
                  onClick={handleEditPrompt}
                  size="small"
                  sx={{
                    color: '#1565c0',
                    '&:hover': { backgroundColor: 'rgba(21, 101, 192, 0.1)' }
                  }}
                  title="Edit Prompt"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              ) : (
                <>
                  <IconButton
                    onClick={savePrompt}
                    disabled={isSavingPrompt}
                    size="small"
                    sx={{
                      color: '#2e7d32',
                      '&:hover': { backgroundColor: 'rgba(46, 125, 50, 0.1)' }
                    }}
                    title="Save Prompt"
                  >
                    {isSavingPrompt ? (
                      <CircularProgress size={16} />
                    ) : (
                      <SaveIcon fontSize="small" />
                    )}
                  </IconButton>
                  <IconButton
                    onClick={handleCancelEdit}
                    disabled={isSavingPrompt}
                    size="small"
                    sx={{
                      color: '#d32f2f',
                      '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' }
                    }}
                    title="Cancel Edit"
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </>
              ))}
          </Box>
        </Box>

        {isLoadingPrompt ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isEditingPrompt && isAdmin() ? (
              <Box>
                <TextField
                  multiline
                  fullWidth
                  rows={15}
                  value={editedPromptContent}
                  onChange={(e) => setEditedPromptContent(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      lineHeight: '1.6',
                      backgroundColor: '#ffffff'
                    }
                  }}
                  placeholder="Enter your prompt content here..."
                  helperText={`${editedPromptContent.length} characters`}
                />

                {/* Action buttons below the text area */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '1rem',
                    marginTop: '1rem',
                    padding: '1rem',
                    backgroundColor: '#f8faff',
                    borderRadius: '8px',
                    border: '1px solid #e3f2fd'
                  }}
                >
                  <Button
                    onClick={handleCancelEdit}
                    disabled={isSavingPrompt}
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    sx={{
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                      '&:hover': {
                        borderColor: '#d32f2f',
                        backgroundColor: 'rgba(211, 47, 47, 0.1)'
                      }
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={savePrompt}
                    disabled={isSavingPrompt || !editedPromptContent.trim()}
                    variant="contained"
                    startIcon={
                      isSavingPrompt ? (
                        <CircularProgress size={16} />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    sx={{
                      backgroundColor: '#2e7d32',
                      '&:hover': {
                        backgroundColor: '#1b5e20'
                      },
                      '&:disabled': {
                        backgroundColor: '#ccc'
                      }
                    }}
                  >
                    {isSavingPrompt ? 'Saving...' : 'Save Prompt'}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    backgroundColor: '#ffffff',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    lineHeight: '1.6',
                    color: '#333',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    position: 'relative'
                  }}
                >
                  <Typography component="div" sx={{ whiteSpace: 'pre-line' }}>
                    {promptData?.content || 'Loading prompt...'}
                  </Typography>
                </Box>

                {/* Edit button below the read-only view - only visible to admins */}
                {isAdmin() && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: '1rem',
                      padding: '1rem',
                      backgroundColor: '#f8faff',
                      borderRadius: '8px',
                      border: '1px solid #e3f2fd'
                    }}
                  >
                    <Button
                      onClick={handleEditPrompt}
                      variant="contained"
                      startIcon={<EditIcon />}
                      sx={{
                        backgroundColor: '#1565c0',
                        '&:hover': {
                          backgroundColor: '#0d47a1'
                        }
                      }}
                    >
                      Edit Prompt
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            <Typography
              variant="body2"
              sx={{
                marginTop: '1rem',
                color: '#666',
                fontStyle: 'italic'
              }}
            >
              {promptData?.description ||
                'This prompt guides our AI system to analyze your compliance documents and generate comprehensive, actionable checklists.'}
              {promptData?.version && (
                <span style={{ marginLeft: '1rem', fontWeight: 'bold' }}>
                  Version: {promptData.version}
                </span>
              )}
            </Typography>
          </>
        )}
      </Box>

      <p>
        <span>Steps to generate checklist from compliance documents:</span>
        <ul style={{ display: 'flex', flexDirection: 'column' }}>
          <li>1. Select the document type (Regulation, Circular, or Other).</li>
          <li>
            2. Upload a PDF file of the compliance document from your computer.
          </li>
          <li>
            3. The system will analyze the document and generate a checklist.
          </li>
          <li>
            4. The generated checklist will be mailed to the email id of the
            user.
          </li>
        </ul>
      </p>

      <div style={{ marginTop: '4rem' }}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'flex-start'
          }}
        >
          {/* File Upload Section */}
          <div
            style={{
              width: '100%',
              padding: '2rem',
              border: '2px dashed #ccc',
              borderRadius: '8px',
              textAlign: 'center',
              backgroundColor: '#fafafa'
            }}
          >
            <input
              type="file"
              id="pdf-upload"
              accept=".pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="pdf-upload">
              <Button
                variant="contained"
                component="span"
                style={{
                  backgroundColor: 'black',
                  marginBottom: '1rem'
                }}
              >
                Choose PDF File
              </Button>
            </label>

            {fileName && (
              <div style={{ marginTop: '1rem' }}>
                <p style={{ margin: 0, fontWeight: 'bold' }}>Selected File:</p>
                <p style={{ margin: 0, color: '#666' }}>{fileName}</p>
              </div>
            )}
          </div>

          <div
            style={{
              width: '100%',
              padding: '1.5rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9'
            }}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
              Document Title
            </h3>
            <p
              style={{
                margin: '0 0 1rem 0',
                color: '#666',
                fontSize: '0.9rem'
              }}
            >
              Enter a descriptive title for your compliance document. This will
              be used as the document name in the system.
            </p>
            <TextField
              fullWidth
              label="Document Title"
              variant="outlined"
              value={documentTitle}
              onChange={(e) => setDocumentTitle(e.target.value)}
              placeholder="e.g., RBI Circular on Digital Lending Guidelines"
              sx={{
                backgroundColor: 'white',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#ddd'
                  },
                  '&:hover fieldset': {
                    borderColor: '#999'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2'
                  }
                }
              }}
            />
          </div>

          {/* Table of Contents Page Range Section */}
          <div
            style={{
              width: '100%',
              padding: '1.5rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9'
            }}
          >
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
              Table of Contents Page Range
            </h3>
            <p
              style={{
                margin: '0 0 1rem 0',
                color: '#666',
                fontSize: '0.9rem'
              }}
            >
              Specify the page range where the table of contents appears in your
              PDF document.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}
              >
                <label
                  htmlFor="toc-start"
                  style={{
                    fontSize: '0.9rem',
                    marginBottom: '0.5rem',
                    fontWeight: '500'
                  }}
                >
                  Start Page:
                </label>
                <input
                  type="number"
                  id="toc-start"
                  min="1"
                  value={tocStartPage}
                  onChange={(e) =>
                    setTocStartPage(parseInt(e.target.value) || 1)
                  }
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    width: '80px',
                    textAlign: 'center'
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}
              >
                <label
                  htmlFor="toc-end"
                  style={{
                    fontSize: '0.9rem',
                    marginBottom: '0.5rem',
                    fontWeight: '500'
                  }}
                >
                  End Page:
                </label>
                <input
                  type="number"
                  id="toc-end"
                  min={tocStartPage}
                  value={tocEndPage}
                  onChange={(e) =>
                    setTocEndPage(parseInt(e.target.value) || tocStartPage)
                  }
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    width: '80px',
                    textAlign: 'center'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              width: '100%',
              marginTop: '1rem'
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="checklist-type-label">Document Type</InputLabel>
              <Select
                labelId="checklist-type-label"
                id="checklist-type-select"
                value={checklistType}
                label="Document Type"
                onChange={handleChecklistTypeChange}
                style={{
                  backgroundColor: 'white'
                }}
              >
                <MenuItem value="REGULATION">Regulation</MenuItem>
                <MenuItem value="CIRCULAR">Circular</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div
            style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '1rem'
            }}
          >
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!selectedFile || !documentTitle.trim() || isUploading}
              style={{
                backgroundColor:
                  !selectedFile || !documentTitle.trim() || isUploading
                    ? '#ccc'
                    : 'black',
                color: 'white',
                minWidth: '150px',
                opacity:
                  !selectedFile || !documentTitle.trim() || isUploading
                    ? 0.6
                    : 1
              }}
            >
              {isUploading ? 'Uploading...' : 'Upload Document'}
            </Button>

            {selectedFile && (
              <Button
                onClick={handleClear}
                variant="outlined"
                style={{
                  borderColor: 'black',
                  color: 'black',
                  minWidth: '150px'
                }}
              >
                Clear Selection
              </Button>
            )}
          </div>

          {/* Status Message */}
          {isUploading && (
            <div style={{ marginTop: '1rem', color: '#666' }}>
              <p>Processing your document... This may take a few moments.</p>
            </div>
          )}
        </div>
      </div>

      {/* Checklist History Section */}
      <Box sx={{ marginTop: '4rem' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem'
          }}
        >
          <Box>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
              Checklist History
            </Typography>
            {!isLoadingHistory && (
              <Typography
                variant="body2"
                sx={{ color: '#666', marginTop: '0.25rem' }}
              >
                {checklistHistory.length} checklist
                {checklistHistory.length !== 1 ? 's' : ''} found
              </Typography>
            )}
          </Box>
          <Button
            onClick={fetchChecklistHistory}
            variant="outlined"
            disabled={isLoadingHistory}
            sx={{
              borderColor: 'black',
              color: 'black',
              '&:hover': {
                borderColor: 'black',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {isLoadingHistory ? <CircularProgress size={20} /> : 'Refresh'}
          </Button>
        </Box>

        {historyError && (
          <Box
            sx={{
              marginBottom: '1rem',
              padding: '1rem',
              backgroundColor: '#ffebee',
              borderRadius: '4px'
            }}
          >
            <Typography color="error">
              Error loading checklist history: {historyError}
            </Typography>
          </Box>
        )}

        {isLoadingHistory ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="checklist history table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    Checklist ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    Document Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    Source Document
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Updated At</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checklistHistory.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: '#666'
                      }}
                    >
                      No checklist history found. Upload a document to get
                      started.
                    </TableCell>
                  </TableRow>
                ) : (
                  checklistHistory.map((checklist) => (
                    <TableRow
                      key={checklist._id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                        >
                          {checklist._id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getChecklistTypeDisplay(
                            checklist.checklistType
                          )}
                          variant="outlined"
                          size="small"
                          sx={{
                            borderColor:
                              checklist.checklistType === 'REGULATION'
                                ? '#1976d2'
                                : checklist.checklistType === 'CIRCULAR'
                                  ? '#ed6c02'
                                  : '#2e7d32',
                            color:
                              checklist.checklistType === 'REGULATION'
                                ? '#1976d2'
                                : checklist.checklistType === 'CIRCULAR'
                                  ? '#ed6c02'
                                  : '#2e7d32'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {checklist.sourceDocumentId?.name ||
                            checklist.sourceDocumentId ||
                            'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={checklist.status}
                          color={getStatusColor(checklist.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(checklist.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(checklist.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {checklist.status === 'COMPLETED' ? (
                          <Button
                            onClick={() =>
                              handleMoveToRepository(checklist._id)
                            }
                            variant="outlined"
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            sx={{
                              color: '#1976d2',
                              borderColor: '#1976d2',
                              '&:hover': {
                                borderColor: '#1976d2',
                                backgroundColor: 'rgba(25, 118, 210, 0.1)'
                              },
                              fontSize: '0.75rem',
                              padding: '4px 8px'
                            }}
                          >
                            Move to Repository
                          </Button>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ color: '#999', fontStyle: 'italic' }}
                          >
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </div>
  );
};

export default ChecklistGenerator;
